import { Button, Col, Icon, Input, Row, Text, Toggle } from '@kognitos/lattice';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ControllerRenderProps, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getNoAutofillProps } from '@/shared/utils';

interface KeyValueRow {
  id: string;
  key: string;
  value: string;
}

export interface CredentialKeyValueInputProps<
  TFieldValues extends FieldValues,
  TName extends
    import('react-hook-form').Path<TFieldValues> = import('react-hook-form').Path<TFieldValues>,
> {
  field: ControllerRenderProps<TFieldValues, TName>;
  disabled?: boolean;
  readOnly?: boolean;
  /** Show a hint that previously saved credentials are hidden. */
  showEditHint?: boolean;
}

let nextRowId = 0;
function makeRowId(): string {
  nextRowId += 1;
  return `kv-${nextRowId}`;
}

function parseRows(json: string): KeyValueRow[] {
  try {
    const obj = JSON.parse(json);
    if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
      return Object.entries(obj).map(([key, value]) => ({
        id: makeRowId(),
        key,
        value: String(value),
      }));
    }
  } catch {
    // invalid JSON — start empty
  }
  return [];
}

function defaultRows(): KeyValueRow[] {
  return [{ id: makeRowId(), key: '', value: '' }];
}

function serializeRows(rows: KeyValueRow[]): string {
  const entries = rows.filter(r => r.key || r.value);
  if (entries.length === 0) return '';
  return JSON.stringify(Object.fromEntries(entries.map(r => [r.key, r.value])));
}

function duplicateKeys(rows: KeyValueRow[]): Set<string> {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const { key } of rows) {
    if (!key) continue;
    if (seen.has(key)) duplicates.add(key);
    seen.add(key);
  }
  return duplicates;
}

const FIELD_COLUMN_FLEX = '1 1 0';
const ICON_COLUMN_FLEX = '36px';

const VALID_KEY_REGEX = /^[A-Za-z0-9]([A-Za-z0-9_-]*[A-Za-z0-9])?$/;

function isValidKey(key: string): boolean {
  if (!key) return true;
  return VALID_KEY_REGEX.test(key);
}

function hasInvalidKeys(rows: KeyValueRow[]): boolean {
  return rows.some(r => r.key && !isValidKey(r.key));
}

function isMissingKey(row: KeyValueRow): boolean {
  return !row.key && !!row.value;
}

/**
 * Validates that all keys in a key/value credential's serialized value are
 * present and conform to the allowed format (alphanumeric, hyphens,
 * underscores; must start/end with alphanumeric). Returns true if valid or
 * empty.
 */
export function hasValidCredentialKeys(jsonString: string): boolean {
  if (!jsonString) return true;
  try {
    const obj = JSON.parse(jsonString);
    if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
      return Object.keys(obj).every(k => !!k && isValidKey(k));
    }
  } catch {
    // not valid JSON
  }
  return true;
}

/**
 * True when any of the given key/value credential form values contains a key
 * that does not conform to the allowed format.
 */
export function hasInvalidKeyValueCredentials(values: unknown[]): boolean {
  return values.some(
    value =>
      typeof value === 'string' && !!value && !hasValidCredentialKeys(value),
  );
}

/**
 * True when any of the given key/value credential form values has a named
 * credential whose value is empty.
 */
export function hasEmptyKeyValueCredentialValues(values: unknown[]): boolean {
  return values.some(value => {
    if (typeof value !== 'string' || !value) return false;
    try {
      const obj = JSON.parse(value);
      return (
        !!obj &&
        typeof obj === 'object' &&
        !Array.isArray(obj) &&
        Object.values(obj).some(v => v === '')
      );
    } catch {
      return false;
    }
  });
}

export function CredentialKeyValueInput({
  field,
  disabled,
  readOnly,
  showEditHint,
}: CredentialKeyValueInputProps<FieldValues, string>) {
  const { t } = useTranslation('integrations');
  const [rows, setRows] = useState<KeyValueRow[]>(() => {
    const parsed = parseRows(field.value || '');
    if (parsed.length > 0 || showEditHint) return parsed;
    return defaultRows();
  });
  const [visibleValues, setVisibleValues] = useState<Record<string, boolean>>(
    {},
  );
  const lastSerialized = useRef(field.value || '');

  // When showEditHint is true and form is read-only, rows are displayed in a
  // masked state: values show as "******" and the eye icon is hidden.
  // The dialog's pencil button toggles readOnly off, which unmasks the rows.
  const showMasked = showEditHint && readOnly;

  // Sync local state when field.value changes externally (e.g. tab switch, form reset)
  useEffect(() => {
    const currentValue = field.value || '';
    if (currentValue !== lastSerialized.current) {
      lastSerialized.current = currentValue;
      const parsed = parseRows(currentValue);
      if (parsed.length > 0 || showEditHint) {
        setRows(parsed);
      } else {
        setRows(defaultRows());
      }
    }
  }, [field.value, showEditHint]);

  const syncToForm = useCallback(
    (updatedRows: KeyValueRow[]) => {
      setRows(updatedRows);
      const serialized = serializeRows(updatedRows);
      lastSerialized.current = serialized;
      field.onChange(serialized);
    },
    [field.onChange],
  );

  const addRow = () => {
    syncToForm([...rows, { id: makeRowId(), key: '', value: '' }]);
  };

  const removeRow = (id: string) => {
    const updated = rows.filter(r => r.id !== id);
    syncToForm(updated);
    setVisibleValues(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const updateRow = (id: string, key: string, value: string) => {
    const updated = rows.map(r => (r.id === id ? { ...r, key, value } : r));
    syncToForm(updated);
  };

  const toggleVisibility = (id: string) => {
    setVisibleValues(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const isInteractive = !disabled && !readOnly && !showMasked;
  const duplicates = duplicateKeys(rows);
  const showDuplicateWarning = duplicates.size > 0;
  const hasKeyError = (row: KeyValueRow) =>
    !showMasked &&
    ((!!row.key && !isValidKey(row.key)) ||
      isMissingKey(row) ||
      duplicates.has(row.key));
  const showInvalidKeyWarning = !showMasked && hasInvalidKeys(rows);
  const showMissingKeyWarning = !showMasked && rows.some(isMissingKey);

  return (
    <div className="space-y-2 w-full" data-testid="kv-credential-editor">
      {showEditHint && rows.length > 0 && (
        <p
          className="text-sm text-muted-foreground"
          data-testid="kv-edit-hint-with-keys"
        >
          {t('integrations.customCredentials.kvEditHintWithKeys')}
        </p>
      )}
      {showEditHint && rows.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="kv-edit-hint">
          {t('integrations.customCredentials.kvEditHint')}
        </p>
      )}
      {isInteractive && !showEditHint && (
        <p
          className="text-xs text-muted-foreground"
          data-testid="kv-key-format-hint"
        >
          {t('integrations.customCredentials.kvKeyFormatHint')}
        </p>
      )}
      {rows.length > 0 && (
        <Row gutter={8} align="center" wrap={false}>
          <Col flex={FIELD_COLUMN_FLEX} className="min-w-0">
            <Text level="small" weight="medium" data-testid="kv-name-label">
              {t('integrations.customCredentials.kvNameLabel')}
            </Text>
          </Col>
          <Col flex={FIELD_COLUMN_FLEX} className="min-w-0">
            <Text level="small" weight="medium" data-testid="kv-value-label">
              {t('integrations.customCredentials.kvValueLabel')}
            </Text>
          </Col>
          {!showMasked && <Col flex={ICON_COLUMN_FLEX} aria-hidden />}
          {isInteractive && <Col flex={ICON_COLUMN_FLEX} aria-hidden />}
        </Row>
      )}
      {rows.map((row, index) => (
        <Row key={row.id} gutter={8} align="center" wrap={false}>
          <Col flex={FIELD_COLUMN_FLEX} className="min-w-0">
            <Input
              data-testid={`kv-key-${index}`}
              type="text"
              placeholder={t('integrations.customCredentials.kvKeyPlaceholder')}
              disabled={disabled}
              readOnly={readOnly || showMasked}
              {...getNoAutofillProps()}
              value={row.key}
              onChange={e => updateRow(row.id, e.target.value, row.value)}
              aria-invalid={hasKeyError(row) || undefined}
            />
          </Col>
          <Col flex={FIELD_COLUMN_FLEX} className="min-w-0">
            <Input
              data-testid={`kv-value-${index}`}
              type={
                showMasked
                  ? 'password'
                  : visibleValues[row.id]
                    ? 'text'
                    : 'password'
              }
              placeholder={
                showMasked
                  ? undefined
                  : t('integrations.customCredentials.kvValuePlaceholder')
              }
              disabled={disabled}
              readOnly={readOnly || showMasked}
              {...getNoAutofillProps({ sensitive: true })}
              value={showMasked ? '******' : row.value}
              onChange={e => updateRow(row.id, row.key, e.target.value)}
            />
          </Col>
          {!showMasked && (
            <Col flex={ICON_COLUMN_FLEX}>
              <Toggle
                data-slot="toggle"
                pressed={!!visibleValues[row.id]}
                onPressedChange={() => toggleVisibility(row.id)}
                aria-label={t(
                  'integrations.customCredentials.showSensitiveValue',
                )}
                variant="outline"
                className="h-10 w-9"
              >
                <Icon type={visibleValues[row.id] ? 'EyeOff' : 'Eye'} />
              </Toggle>
            </Col>
          )}
          {isInteractive && (
            <Col flex={ICON_COLUMN_FLEX}>
              <Button
                data-testid={`kv-remove-${index}`}
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeRow(row.id)}
                aria-label={t('integrations.customCredentials.kvRemoveTooltip')}
              >
                <Icon type="Trash" />
              </Button>
            </Col>
          )}
        </Row>
      ))}
      {showDuplicateWarning && (
        <p
          className="text-sm text-destructive"
          data-testid="kv-duplicate-warning"
        >
          {t('integrations.customCredentials.kvDuplicateWarning')}
        </p>
      )}
      {showInvalidKeyWarning && (
        <p
          className="text-sm text-destructive"
          data-testid="kv-invalid-key-warning"
        >
          {t('integrations.customCredentials.kvInvalidKeyWarning')}
        </p>
      )}
      {showMissingKeyWarning && (
        <p
          className="text-sm text-destructive"
          data-testid="kv-missing-key-warning"
        >
          {t('integrations.customCredentials.kvMissingKeyWarning')}
        </p>
      )}
      {isInteractive && (
        <Button
          data-testid="kv-add-button"
          type="button"
          variant="outline"
          size="sm"
          onClick={addRow}
        >
          <Icon type="Plus" className="mr-1" />
          {t('integrations.customCredentials.kvAddButton')}
        </Button>
      )}
    </div>
  );
}
